import { DownloadOutlined, FilterOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Col, DatePicker, Form, Row, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import * as XLSX from 'xlsx';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { formatNumber, handleAPIError } from '../../utils/helpers';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface RevenueReportRow {
	retailer_id: number | string;
	retailer_name: string;
	vendor_id: string;
	sold_count: number;
	completed_count: number;
	recovered_count: number;
	pending_count: number;
	total_zazu_fee: number;
	total_interest_collected: number;
	total_zf_paid_to_retailer: number;
	total_cash_in_hand: number;
	total_amount_paid: number;
	net_revenue: number;
	collection_pct: number;
}

interface RevenueReportTotals extends RevenueReportRow {
	collected_penalty: number;
}

interface RevenueReportResponse {
	retailers: RevenueReportRow[];
	totals: RevenueReportTotals;
	count: number;
	page: number;
	limit: number;
}

interface FilterValues {
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const ugx = (v: number) => `UGX ${formatNumber(v)}`;

const withTooltip = (label: string, tip: string) => (
	<span>
		{label}{' '}
		<Tooltip title={tip}>
			<InfoCircleOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />
		</Tooltip>
	</span>
);

const collectionColor = (pct: number) => {
	if (pct >= 80) return '#52c41a';
	if (pct >= 50) return '#faad14';
	return '#ff4d4f';
};

const quickPresets: { label: string; value: [dayjs.Dayjs, dayjs.Dayjs] }[] = [
	{ label: 'This Week', value: [dayjs().startOf('week'), dayjs()] },
	{ label: 'This Month', value: [dayjs().startOf('month'), dayjs()] },
	{ label: 'This Year', value: [dayjs().startOf('year'), dayjs()] },
];

const RevenueReport = () => {
	const [form] = Form.useForm<FilterValues>();
	const [pagination, setPagination] = useState({ page: 1, pageSize: 20 });
	const [filters, setFilters] = useState<FilterValues>({
		date_range: [dayjs().startOf('year'), dayjs()],
	});

	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ['revenue-report', pagination, filters],
		queryFn: async () => {
			const response = await axios.get('/admin/reports/revenue', {
				withCredentials: true,
				params: {
					page: pagination.page,
					limit: pagination.pageSize,
					start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
					end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
				},
			});
			return response.data.results as RevenueReportResponse;
		},
	});

	if (error) handleAPIError(error);

	const rows = data?.retailers ?? [];
	const totals = data?.totals as RevenueReportTotals | undefined;

	const handleFilter = () => {
		setFilters(form.getFieldsValue());
		setPagination({ ...pagination, page: 1 });
	};

	const handleReset = () => {
		const defaultRange: [dayjs.Dayjs, dayjs.Dayjs] = [dayjs().startOf('year'), dayjs()];
		form.setFieldsValue({ date_range: defaultRange });
		setFilters({ date_range: defaultRange });
		setPagination({ page: 1, pageSize: 20 });
	};

	const handleExport = () => {
		if (!rows.length) return;

		const exportRows = rows.map((r) => ({
			'Retailer': r.retailer_name,
			'Vendor ID': r.vendor_id,
			'Sold': r.sold_count,
			'Completed': r.completed_count,
			'Recovered': r.recovered_count,
			'Pending': r.pending_count,
			'Zazu Fee (UGX)': r.total_zazu_fee,
			'Interest Collected (UGX)': r.total_interest_collected,
			'ZF Paid to Retailer (UGX)': r.total_zf_paid_to_retailer,
			'Cash at Hand (UGX)': r.total_cash_in_hand,
			'Total Collected (UGX)': r.total_amount_paid,
			'Net Revenue (UGX)': r.net_revenue,
			'Collection %': r.collection_pct,
		}));

		if (totals) {
			exportRows.push({
				'Retailer': 'TOTALS',
				'Vendor ID': '',
				'Sold': totals.sold_count,
				'Completed': totals.completed_count,
				'Recovered': totals.recovered_count,
				'Pending': totals.pending_count,
				'Zazu Fee (UGX)': totals.total_zazu_fee,
				'Interest Collected (UGX)': totals.total_interest_collected,
				'ZF Paid to Retailer (UGX)': totals.total_zf_paid_to_retailer,
				'Cash at Hand (UGX)': totals.total_cash_in_hand,
				'Total Collected (UGX)': totals.total_amount_paid,
				'Net Revenue (UGX)': totals.net_revenue,
				'Collection %': totals.collection_pct,
			});
		}

		const sheet = XLSX.utils.json_to_sheet(exportRows);
		sheet['!cols'] = [
			{ wch: 26 }, { wch: 12 }, { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 10 },
			{ wch: 18 }, { wch: 20 }, { wch: 24 }, { wch: 20 }, { wch: 18 }, { wch: 14 },
		];
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, sheet, 'Revenue');
		const start = filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : 'all';
		const end = filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : 'now';
		XLSX.writeFile(wb, `revenue-report-${start} to ${end}.xlsx`);
	};

	const columns: ColumnsType<RevenueReportRow> = [
		{
			title: 'Retailer',
			dataIndex: 'retailer_name',
			key: 'retailer_name',
			width: 200,
			fixed: 'left',
			sorter: (a, b) => a.retailer_name.localeCompare(b.retailer_name),
		},
		{
			title: 'Sold',
			dataIndex: 'sold_count',
			key: 'sold_count',
			width: 80,
			align: 'center',
			sorter: (a, b) => a.sold_count - b.sold_count,
		},
		{
			title: 'Completed',
			dataIndex: 'completed_count',
			key: 'completed_count',
			width: 110,
			align: 'center',
			render: (v: number) => <Text style={{ color: '#52c41a' }}>{v}</Text>,
			sorter: (a, b) => a.completed_count - b.completed_count,
		},
		{
			title: 'Recovered',
			dataIndex: 'recovered_count',
			key: 'recovered_count',
			width: 110,
			align: 'center',
			render: (v: number) => <Text style={{ color: '#595959' }}>{v}</Text>,
			sorter: (a, b) => a.recovered_count - b.recovered_count,
		},
		{
			title: 'Pending',
			dataIndex: 'pending_count',
			key: 'pending_count',
			width: 90,
			align: 'center',
			render: (v: number) => <Text style={{ color: '#faad14' }}>{v}</Text>,
			sorter: (a, b) => a.pending_count - b.pending_count,
		},
		{
			title: withTooltip('Zazu Fee', '4.2% management + monitoring fee earned on every contract'),
			dataIndex: 'total_zazu_fee',
			key: 'total_zazu_fee',
			width: 160,
			align: 'right',
			render: (v: number) => ugx(v),
			sorter: (a, b) => a.total_zazu_fee - b.total_zazu_fee,
		},
		{
			title: withTooltip('Interest Collected', '30% collected at down payment for all contracts; remaining 70% only when contract is completed'),
			dataIndex: 'total_interest_collected',
			key: 'total_interest_collected',
			width: 170,
			align: 'right',
			render: (v: number) => ugx(v),
			sorter: (a, b) => a.total_interest_collected - b.total_interest_collected,
		},
		{
			title: withTooltip('ZF Paid to Retailer', 'Capital disbursed to retailer upfront by Zazu'),
			dataIndex: 'total_zf_paid_to_retailer',
			key: 'total_zf_paid_to_retailer',
			width: 190,
			align: 'right',
			render: (v: number) => ugx(v),
			sorter: (a, b) => a.total_zf_paid_to_retailer - b.total_zf_paid_to_retailer,
		},
		{
			title: withTooltip('Cash at Hand', 'Down payment collected from customer at contract start'),
			dataIndex: 'total_cash_in_hand',
			key: 'total_cash_in_hand',
			width: 160,
			align: 'right',
			render: (v: number) => ugx(v),
			sorter: (a, b) => a.total_cash_in_hand - b.total_cash_in_hand,
		},
		{
			title: 'Total Collected',
			dataIndex: 'total_amount_paid',
			key: 'total_amount_paid',
			width: 170,
			align: 'right',
			render: (v: number) => ugx(v),
			sorter: (a, b) => a.total_amount_paid - b.total_amount_paid,
		},
		{
			title: 'Net Revenue',
			dataIndex: 'net_revenue',
			key: 'net_revenue',
			width: 170,
			align: 'right',
			defaultSortOrder: 'descend',
			render: (v: number) => (
				<Text strong style={{ color: v >= 0 ? '#52c41a' : '#ff4d4f' }}>
					{ugx(v)}
				</Text>
			),
			sorter: (a, b) => a.net_revenue - b.net_revenue,
		},
		{
			title: 'Collection %',
			dataIndex: 'collection_pct',
			key: 'collection_pct',
			width: 120,
			align: 'center',
			render: (v: number) => (
				<Tag color={v >= 80 ? 'green' : v >= 50 ? 'orange' : 'red'}>{v}%</Tag>
			),
			sorter: (a, b) => a.collection_pct - b.collection_pct,
		},
	];

	return (
		<AppLayout title="Revenue Report" subtitle="Zazu's income breakdown by retailer">
			{/* Summary Cards */}
			{totals && (
				<Card className="mb-6">
					<Title level={5} className="mb-4">
						Revenue Summary
					</Title>
					<Row gutter={[16, 16]}>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Total Contracts"
								value={totals.sold_count}
								valueStyle={{ color: '#1890ff' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="Zazu Fees"
								value={totals.total_zazu_fee}
								formatter={(v) => ugx(v as number)}
								valueStyle={{ color: '#722ed1' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Tooltip title="30% of all contracts' interest (down payment) + 70% of completed contracts only">
								<Statistic
									title="Interest Collected"
									value={totals.total_interest_collected}
									formatter={(v) => ugx(v as number)}
									valueStyle={{ color: '#13c2c2' }}
								/>
							</Tooltip>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Statistic
								title="ZF Paid Out"
								value={totals.total_zf_paid_to_retailer}
								formatter={(v) => ugx(v as number)}
								valueStyle={{ color: '#fa8c16' }}
							/>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Tooltip title="Sum of penalty_amount on paid installments only — excludes accumulated uncollected penalties">
								<Statistic
									title="Penalties Collected"
									value={totals.collected_penalty}
									formatter={(v) => ugx(v as number)}
									valueStyle={{ color: totals.collected_penalty > 0 ? '#52c41a' : undefined }}
								/>
							</Tooltip>
						</Col>
						<Col xs={12} sm={8} md={4}>
							<Tooltip title="Total Collected − ZF Paid to Retailer − Cash at Hand">
								<Statistic
									title="Net Revenue"
									value={totals.net_revenue}
									formatter={(v) => ugx(v as number)}
									valueStyle={{ color: totals.net_revenue >= 0 ? '#52c41a' : '#ff4d4f' }}
								/>
							</Tooltip>
						</Col>
					</Row>
				</Card>
			)}

			{/* Filters */}
			<Card className="mb-4">
				<div className="flex flex-wrap items-center gap-3">
					<Form
						form={form}
						layout="inline"
						initialValues={{ date_range: [dayjs().startOf('year'), dayjs()] }}
					>
						<Form.Item name="date_range" label="Date Range">
							<RangePicker style={{ width: 280 }} placeholder={['Start Date', 'End Date']} />
						</Form.Item>
					</Form>
					<Space wrap>
						{quickPresets.map((p) => (
							<Button
								key={p.label}
								size="small"
								onClick={() => {
									form.setFieldsValue({ date_range: p.value });
									setFilters({ date_range: p.value });
									setPagination({ ...pagination, page: 1 });
								}}
							>
								{p.label}
							</Button>
						))}
					</Space>
					<Space>
						<Button type="primary" icon={<FilterOutlined />} onClick={handleFilter}>
							Apply
						</Button>
						<Button icon={<ReloadOutlined />} onClick={handleReset}>
							Reset
						</Button>
						<Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!rows.length}>
							Export
						</Button>
						<Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
							Refresh
						</Button>
					</Space>
				</div>
			</Card>

			{/* Table */}
			<Card>
				<Table
					columns={columns}
					dataSource={rows}
					loading={isLoading}
					rowKey="retailer_id"
					scroll={{ x: 1800 }}
					pagination={{
						current: pagination.page,
						pageSize: pagination.pageSize,
						total: data?.count ?? 0,
						onChange: (page, pageSize) => setPagination({ page, pageSize: pageSize ?? 20 }),
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} retailers`,
					}}
					summary={() =>
						totals ? (
							<Table.Summary fixed>
								<Table.Summary.Row style={{ fontWeight: 600, background: '#fafafa' }}>
									<Table.Summary.Cell index={0}>Totals</Table.Summary.Cell>
									<Table.Summary.Cell index={1} align="center">{totals.sold_count}</Table.Summary.Cell>
									<Table.Summary.Cell index={2} align="center">
										<Text style={{ color: '#52c41a' }}>{totals.completed_count}</Text>
									</Table.Summary.Cell>
									<Table.Summary.Cell index={3} align="center">
										<Text style={{ color: '#595959' }}>{totals.recovered_count}</Text>
									</Table.Summary.Cell>
									<Table.Summary.Cell index={4} align="center">
										<Text style={{ color: '#faad14' }}>{totals.pending_count}</Text>
									</Table.Summary.Cell>
									<Table.Summary.Cell index={5} align="right">{ugx(totals.total_zazu_fee)}</Table.Summary.Cell>
									<Table.Summary.Cell index={6} align="right">{ugx(totals.total_interest_collected)}</Table.Summary.Cell>
									<Table.Summary.Cell index={7} align="right">{ugx(totals.total_zf_paid_to_retailer)}</Table.Summary.Cell>
									<Table.Summary.Cell index={8} align="right">{ugx(totals.total_cash_in_hand)}</Table.Summary.Cell>
									<Table.Summary.Cell index={9} align="right">{ugx(totals.total_amount_paid)}</Table.Summary.Cell>
									<Table.Summary.Cell index={10} align="right">
										<Text strong style={{ color: totals.net_revenue >= 0 ? '#52c41a' : '#ff4d4f' }}>
											{ugx(totals.net_revenue)}
										</Text>
									</Table.Summary.Cell>
									<Table.Summary.Cell index={11} align="center">
										<Tag color={collectionColor(totals.collection_pct) === '#52c41a' ? 'green' : collectionColor(totals.collection_pct) === '#faad14' ? 'orange' : 'red'}>
											{totals.collection_pct}%
										</Tag>
									</Table.Summary.Cell>
								</Table.Summary.Row>
							</Table.Summary>
						) : null
					}
				/>
			</Card>
		</AppLayout>
	);
};

export default RevenueReport;
